/**
 * Leads React Query Hooks
 * 
 * This module provides React Query hooks for the Leads module:
 * - useLeadsList: List with filters, pagination, sorting
 * - useLead: Single lead detail
 * - useLeadFilters: Filter options
 * - useLeadHousehold: Household members for a lead
 * - useLeadMutations: CRUD operations with cache invalidation
 * 
 * All queries include tenant_id in the query key to prevent cross-tenant cache bleed.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useTenant } from "@/contexts/TenantContext";

/**
 * Serialize filter object to a stable query param format
 */
const serializeFilters = (obj) => {
  const clauses = [];
  Object.entries(obj || {}).forEach(([field, cfg]) => {
    if (!cfg?.enabled) return;
    const operator = cfg.operator;
    if (!operator) return;
    
    const dateNoValueOps = new Set([
      "this_week", "this_month", "this_year",
      "last_week", "last_month", "last_year",
      "last_30_days", "last_60_days", "last_90_days", "ytd",
    ]);

    if (["is_empty", "is_not_empty"].includes(operator) || dateNoValueOps.has(operator)) {
      clauses.push({ field, operator });
      return;
    }
    if (cfg.values && Array.isArray(cfg.values) && cfg.values.length > 0) {
      clauses.push({ field, operator, values: cfg.values });
      return;
    }
    const v = String(cfg.value || "").trim();
    if (!v) return;

    if (operator === "between") {
      const vTo = String(cfg.value_to || "").trim();
      if (vTo) clauses.push({ field, operator, value: v, value_to: vTo });
      return;
    }

    clauses.push({ field, operator, value: v });
  });
  return clauses;
};

/**
 * Hook: useLeadsList
 * Fetches paginated, filtered, sorted leads list
 */
export function useLeadsList({
  filters = {},
  search = "",
  page = 0,
  pageSize = 10,
  sortBy = null,
  sortDir = "desc",
  includeHouseholdMembers = false,
  enabled = true,
}) {
  const { selectedTenantId } = useTenant();
  
  // Create a stable key from filters
  const filtersKey = JSON.stringify(serializeFilters(filters));
  
  return useQuery({
    queryKey: queryKeys.leads.list(
      selectedTenantId,
      filtersKey,
      { offset: page * pageSize, limit: pageSize, includeHouseholdMembers },
      { sortBy, sortDir }
    ),
    queryFn: async () => {
      const res = await api.get("/leads", {
        params: {
          q: search || undefined,
          filters: filtersKey,
          sort_by: sortBy || undefined,
          sort_dir: sortBy ? sortDir : undefined,
          include_household_members: includeHouseholdMembers,
          limit: pageSize,
          offset: page * pageSize,
        },
      });
      return {
        items: res.data.items || [],
        total: res.data.total || 0,
      };
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook: useLeadFilters
 * Fetches filter options for leads
 */
export function useLeadFilters() {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: queryKeys.leads.filters(selectedTenantId),
    queryFn: async () => {
      const res = await api.get("/leads/filters");
      return res.data || {
        campaign_names: [],
        form_names: [],
        lead_sources: [],
        statuses: [],
        states: [],
        counties: [],
        lead_types: [],
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - filter options don't change often
  });
}

/**
 * Hook: useLead
 * Fetches a single lead by ID
 */
export function useLead(leadId, { enabled = true } = {}) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: queryKeys.leads.detail(selectedTenantId, leadId),
    queryFn: async () => {
      const res = await api.get(`/leads/${leadId}`);
      return res.data;
    },
    enabled: enabled && Boolean(leadId),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook: useLeadHousehold
 * Fetches household members for a lead
 */
export function useLeadHousehold(leadId, { enabled = true } = {}) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: queryKeys.leads.household(selectedTenantId, leadId),
    queryFn: async () => {
      const res = await api.get(`/leads/${leadId}/household-members`);
      return res.data || null;
    },
    enabled: enabled && Boolean(leadId),
    staleTime: 60 * 1000, // 1 minute for household data
  });
}

/**
 * Hook: useLeadMutations
 * Provides mutation functions for leads with cache invalidation
 */
export function useLeadMutations() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  
  // Create lead
  const createLead = useMutation({
    mutationFn: async (data) => {
      const res = await api.post("/leads", data);
      return res.data;
    },
    onSuccess: () => {
      // Invalidate the leads list
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.all(selectedTenantId),
      });
      // Invalidate filter options (new lead might add new filter values)
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.filters(selectedTenantId),
      });
    },
  });
  
  // Update lead
  const updateLead = useMutation({
    mutationFn: async ({ leadId, data }) => {
      const res = await api.put(`/leads/${leadId}`, data);
      return res.data;
    },
    onSuccess: (data, { leadId }) => {
      // Update the specific lead in cache
      queryClient.setQueryData(
        queryKeys.leads.detail(selectedTenantId, leadId),
        data
      );
      // Invalidate the list to reflect any changes
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.all(selectedTenantId),
      });
      // Also invalidate appointments that might reference this lead
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(selectedTenantId),
      });
    },
  });
  
  // Delete lead
  const deleteLead = useMutation({
    mutationFn: async (leadId) => {
      await api.delete(`/leads/${leadId}`);
      return leadId;
    },
    onSuccess: (leadId) => {
      // Remove the lead from cache
      queryClient.removeQueries({
        queryKey: queryKeys.leads.detail(selectedTenantId, leadId),
      });
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.all(selectedTenantId),
      });
    },
  });
  
  // Bulk delete leads
  const bulkDeleteLeads = useMutation({
    mutationFn: async (leadIds) => {
      await Promise.all(leadIds.map((id) => api.delete(`/leads/${id}`)));
      return leadIds;
    },
    onSuccess: (leadIds) => {
      // Remove each lead from cache
      leadIds.forEach((leadId) => {
        queryClient.removeQueries({
          queryKey: queryKeys.leads.detail(selectedTenantId, leadId),
        });
      });
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.all(selectedTenantId),
      });
    },
  });
  
  // Convert lead to appointment
  const convertLead = useMutation({
    mutationFn: async (leadId) => {
      const res = await api.post(`/leads/${leadId}/convert`, {});
      return res.data;
    },
    onSuccess: (data, leadId) => {
      // Invalidate lead detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.detail(selectedTenantId, leadId),
      });
      // Invalidate leads list
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.all(selectedTenantId),
      });
      // Invalidate appointments (new appointment created)
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(selectedTenantId),
      });
    },
  });
  
  return {
    createLead,
    updateLead,
    deleteLead,
    bulkDeleteLeads,
    convertLead,
  };
}

/**
 * Hook: useInvalidateLeads
 * Returns a function to invalidate all leads-related queries
 */
export function useInvalidateLeads() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  
  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.leads.all(selectedTenantId),
    });
  };
}

export default {
  useLeadsList,
  useLeadFilters,
  useLead,
  useLeadHousehold,
  useLeadMutations,
  useInvalidateLeads,
};
